import React from 'react'
import { Box, Typography } from '@mui/material'
import { FormProvider, type UseFormReturn } from 'react-hook-form'
import * as yup from 'yup'
import GenericDateTimePicker from '../../../../components/GenericDateTimePicker'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import { BULK_ACTION_WARN_THRESHOLD, isBulkReopenScheduleEnabled, type ReopenTargetStatus } from '../bulkSectionStatusActions'
import { anHourAwayMessage, isAtLeastAnHourAway } from '../reopenDateRules'

/**
 * The confirm step of Bulk Reopen Surveys: how many sections are being reopened, and the one set
 * of dates applied to all of them.
 *
 * The dates live here rather than in the bulk-select table because a per-row date input would be
 * far heavier than any existing bulk flow (spec, Frozen Decisions). Fields, labels and rules match
 * the per-row ReopenSurveyModal so the two surfaces read the same; that modal is deliberately left
 * untouched (spec, Out Of Scope), which is why the rules are restated rather than shared.
 *
 * These rules are the client-side half only. The backend validates the shared end date against
 * *each* section's own boundaries, so a date accepted here can still be refused for some sections —
 * that is reported per section when the task lands, not predicted here.
 *
 * Spec: docs/specs/active/bulk-reopen-closed-section-surveys/spec.md
 */

export interface BulkReopenDates {
  endTimestamp: string
  earlyAccessReleaseTimestamp1?: string
  earlyAccessReleaseTimestamp2?: string
  /** Only ever set in `published` mode: a survey opening now has no start date to choose. */
  startTimestamp?: string
}

export interface BulkReopenFormValues {
  startTimestamp: Date | null
  endTimestamp: Date | null
  earlyAccessReleaseTimestamp1: Date | null
  earlyAccessReleaseTimestamp2: Date | null
}

interface BulkReopenConfirmProps {
  selectionCount: number
  /**
   * Which branch of the fork this form is serving. `published` adds the start date and rewords the
   * lede and the confirm button; every other field is identical, which is why this is one form
   * with a mode rather than two.
   */
  mode: ReopenTargetStatus
  /**
   * The form, owned by the parent rather than created here.
   *
   * This step unmounts when the admin steps back to the fork — that is what makes Back cheap for
   * the selection. It also threw away every date they had typed, because the form went with it: fill
   * an end date and both release dates on Go Live, realise you meant to schedule, and all three are
   * gone even though those fields are identical in both branches. Held above the step, the values
   * outlive it. Found by review.
   */
  methods: UseFormReturn<BulkReopenFormValues>
  onConfirm: (dates: BulkReopenDates) => Promise<void> | void
  onCancel: () => void
  isSubmitting?: boolean
  /**
   * A refusal the backend returned for these dates, shown in place rather than as a toast.
   *
   * The endpoint validates the shared dates on the request now (Backend #2164): every rule reads
   * only the submitted dates and the project's bounds, so the answer cannot vary across the batch
   * and is known before a task exists. That makes the refusal something the admin can act on
   * immediately — they are still looking at the field that caused it.
   */
  submitError?: string | null
}

export const buildValidationSchema = (mode: ReopenTargetStatus): yup.ObjectSchema<BulkReopenFormValues> => yup.object({
  startTimestamp: mode === 'published'
    ? yup
      .date()
      .typeError('Start date is required')
      .test('start-an-hour-away', anHourAwayMessage('Start date'), isAtLeastAnHourAway)
      .test(
        'start-an-hour-before-end',
        'Start date must be at least one hour before the end date',
        function (value) {
          // An hour, not merely "before": the backend refuses a survey whose window is shorter
          // than that, reporting it as `latest_allowed_start_timestamp`. Checking only `start <
          // end` here let a 30-minute window through the form and fail per section instead.
          const end = this.parent.endTimestamp as Date | null | undefined
          if (!value || !end) return true
          return end.getTime() - value.getTime() >= 60 * 60 * 1000
        }
      )
      .nullable()
      .required('Start date is required')
    : yup.date().nullable(),
  endTimestamp: yup
    .date()
    .typeError('End date is required')
    .test('end-an-hour-away', anHourAwayMessage('End date'), isAtLeastAnHourAway)
    .nullable()
    .required('End date is required'),
  earlyAccessReleaseTimestamp1: yup
    .date()
    .typeError('Early access release date is required')
    .test('admin-release-an-hour-away', anHourAwayMessage('Early access release date'), isAtLeastAnHourAway)
    .nullable(),
  earlyAccessReleaseTimestamp2: yup
    .date()
    .typeError('Early access release date is required')
    .test('instructor-release-an-hour-away', anHourAwayMessage('Early access release date'), isAtLeastAnHourAway)
    .nullable()
}) as yup.ObjectSchema<BulkReopenFormValues>

const BulkReopenConfirm: React.FC<BulkReopenConfirmProps> = ({
  selectionCount,
  mode,
  methods,
  onConfirm,
  onCancel,
  isSubmitting = false,
  submitError = null
}) => {
  const isScheduled = mode === 'published'
  /**
   * The sentence that says what this branch does, shown only when there is a branch.
   *
   * With the fork switched off there is one behaviour and the admin did not choose it, so adding
   * a sentence about it would change the copy of the reopen that ships today — which is exactly
   * what this feature promised not to do until the backend contract lands. Found by review on the
   * master-to-test PR: the confirm and Back wording were gated and this was not.
   */
  const modeSentence = !isBulkReopenScheduleEnabled()
    ? ''
    : isScheduled
      ? 'They open on the start date and show as Upcoming until then, so you can still make changes.'
      : 'Students start responding as soon as you confirm.'
  const { handleSubmit } = methods

  const handleFormSubmit = async (values: BulkReopenFormValues): Promise<void> => {
    if (!values.endTimestamp) return
    // GenericDateTimePicker already stores each field as the true UTC instant of the picked time,
    // so serialize directly — the same reasoning as ReopenSurveyModal, where subtracting the
    // browser offset double-shifted the value.
    await onConfirm({
      endTimestamp: values.endTimestamp.toISOString(),
      earlyAccessReleaseTimestamp1: values.earlyAccessReleaseTimestamp1?.toISOString(),
      earlyAccessReleaseTimestamp2: values.earlyAccessReleaseTimestamp2?.toISOString(),
      // Only in `published` mode, even if a stale value were somehow in the form: sending a start
      // date on a live reopen would ask the endpoint for a schedule the admin did not choose.
      ...(isScheduled ? { startTimestamp: values.startTimestamp?.toISOString() } : {})
    })
  }

  return (
    <FormProvider {...methods}>
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Box display='flex' flexDirection='column' gap={4}>
          <Typography fontSize='14px'>
            {[
              `Reopen ${selectionCount.toLocaleString()} course section survey${selectionCount === 1 ? '' : 's'}?`,
              modeSentence,
              'These dates are applied to every selected course section.'
            ].filter(Boolean).join(' ')}
          </Typography>
          {selectionCount > BULK_ACTION_WARN_THRESHOLD && (
            <Typography fontSize='14px' fontWeight={500} role='alert'>
              This will {isScheduled ? 'schedule' : 'reopen'} {selectionCount.toLocaleString()} course section surveys and cannot be undone. Go back and narrow the filter if that is more than you intended.
            </Typography>
          )}
          <Typography fontSize='14px' color='text.secondary'>
            Re-opening these course section surveys may require pushing back the admin and instructor results release dates. If the release dates are delayed, instructor and admin access to results will be temporarily hidden until the new release dates are reached.
          </Typography>
          {isScheduled && (
            <GenericDateTimePicker name='startTimestamp' label='New Start Date' required minDate={new Date()} />
          )}
          <GenericDateTimePicker name='endTimestamp' label='New End Date' required minDate={new Date()} />
          <GenericDateTimePicker name='earlyAccessReleaseTimestamp1' label='New Admin Results Release Date' minDate={new Date()} />
          <GenericDateTimePicker name='earlyAccessReleaseTimestamp2' label='New Instructor Results Release Date' minDate={new Date()} />
        </Box>
        {submitError !== null && (
          <Typography
            role='alert'
            fontSize='14px'
            color='error.main'
            sx={{ whiteSpace: 'pre-line', mt: 2 }}
          >
            {submitError}
          </Typography>
        )}
        <DefaultModalActions
          confirmButtonProps={{
            text: isScheduled
              ? 'Reopen & publish'
              : isBulkReopenScheduleEnabled() ? 'Reopen & go live' : 'Reopen',
            type: 'submit',
            loading: isSubmitting,
            // `loading` alone leaves a live `type="submit"` button behind — see the guard in the
            // parent's `submitStatusChange`. This is the affordance; that is the protection.
            disabled: isSubmitting
          }}
          // Back where there is a fork to go back to -- it returns to the mode step with the
          // selection intact, rather than discarding a Select All the admin spent filters building.
          // With the fork off there is nothing behind this step, so the button keeps its old
          // meaning and its old word.
          destructiveButtonProps={{
            text: isBulkReopenScheduleEnabled() ? 'Back' : 'Cancel',
            onClick: onCancel
          }}
        />
      </form>
    </FormProvider>
  )
}

export default BulkReopenConfirm
